import { GlobalConfigType } from 'actions'
import { ILoanOriginSection, InputType, isInputToTriggerBlur, IVisibleProp } from 'config'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getCustomInformation, updateCustomInformationField } from 'services'
import { InputConvert, InputValidate } from 'utils'
import { getInvisibleFields, isReadOnlyByEditPermission, isViewOnlyByLogic, isVisibleFieldVisibleProps } from 'utils/loan'
import { getFieldSpanClass, getNumberOfGridClass } from 'utils/loanFieldSpan'
import { resolveLinkedFieldValue } from 'utils/loanLinkedField'
import { RenderInput } from 'utils/RenderInput'

interface CustomInformationPageProps {
  menuKey: string
  setLoading?: (loading: boolean) => void
}

const buildInputs = (
  fieldProperties: Record<string, IVisibleProp>,
  data: Record<string, any>,
  menuKey: string,
): Record<string, InputType> => {
  const invisibleFields = getInvisibleFields(menuKey as any)

  const inputs: Record<string, InputType> = {}
  Object.keys(fieldProperties).forEach((key) => {
    const prop = fieldProperties[key]
    const input: InputType = { inputType: 'text', ...prop } as any

    if (invisibleFields.includes(key)) {
      input.visible = false
      inputs[key] = input
      return
    }

    const value = data[key] ?? ''
    // Linked fields are read-only mirrors — skip validating the local stored copy.
    const linked = prop?.linkedField
    const isLinkedField = !!(linked?.fieldOrigin && linked?.fieldKey)
    input.error = isLinkedField ? '' : InputValidate({ ...input, value })

    // Filter select options by their own visibleLogic
    let options = input.options as any
    if (options && Array.isArray(options)) {
      try {
        options = options.filter((option: any) => {
          if (!option.visibleLogic) return true
          return isVisibleFieldVisibleProps({ visibleLogic: option.visibleLogic })
        })
      } catch {
        options = []
      }
      input.options = options
    }

    if (prop?.tooltip) input.tooltip = prop.tooltip

    inputs[key] = input
  })

  return inputs
}

export const CustomInformationPage = ({ menuKey, setLoading }: CustomInformationPageProps) => {
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [data, setData] = useState<Record<string, any>>({})
  const pendingBlur = useRef<{ key: string; value: any } | null>(null)

  const { loan, fieldProperties, loanFieldSections, pageProperties } = useSelector((state: any) => {
    const loanFields = state.globalConfigReducer[GlobalConfigType.LoanFields] || {}
    return {
      loan: state.loan,
      fieldProperties: (loanFields[menuKey] || {}) as Record<string, IVisibleProp>,
      loanFieldSections: ((loanFields['sections'] || {})[menuKey] || []) as ILoanOriginSection[],
      pageProperties: (loanFields.properties || {})[menuKey] || {},
    }
  })

  const numberOfGrid = pageProperties.numberOfGrid ?? 2
  const gridClass = getNumberOfGridClass(numberOfGrid)

  const freeSectionFields = useMemo(
    () => Object.keys(fieldProperties).filter((key) => !loanFieldSections.find((s) => s.fields.includes(key))),
    [fieldProperties, loanFieldSections],
  )

  // Rebuild inputs whenever field definitions or saved data change
  useEffect(() => {
    setInputs(buildInputs(fieldProperties, data, menuKey))
  }, [menuKey, fieldProperties, data])

  useEffect(() => {
    getCustomInformation(menuKey).then((res) => setData(res || {}))
  }, [menuKey])

  const onChange = async (key: string, value: any) => {
    value = InputConvert(inputs[key], value)
    setData((prev) => ({ ...prev, [key]: value }))
    if (isInputToTriggerBlur(inputs[key])) {
      await onBlur(key, value)
    } else {
      pendingBlur.current = { key, value }
    }
  }

  const onBlur = async (key: string, value: any = null) => {
    const effectiveValue = value ?? pendingBlur.current?.value ?? data[key] ?? ''
    if (pendingBlur.current?.key === key) pendingBlur.current = null

    const input = inputs[key]
    if (!input || (input.error && input.error.length > 0)) return

    setLoading?.(true)
    await updateCustomInformationField(menuKey, key, effectiveValue)
    setLoading?.(false)
  }

  const renderInput = (key: string, index: number) => {
    const input = inputs[key]
    if (!input || input.visible === false) return null

    const linked = resolveLinkedFieldValue(fieldProperties[key], loan)
    const currentInput = { ...input }

    if (linked.resolved) {
      currentInput.value = linked.value
      currentInput.readOnly = true
    } else {
      currentInput.value = data[key] ?? ''
    }

    if (isViewOnlyByLogic(fieldProperties[key]?.viewOnlyLogic)) currentInput.readOnly = true
    if (!currentInput.readOnly && isReadOnlyByEditPermission(fieldProperties[key]?.editPermissionAccountTypes))
      currentInput.readOnly = true

    if (fieldProperties[key]?.title) currentInput.title = fieldProperties[key].title || currentInput.title
    if (fieldProperties[key]?.type) currentInput.type = (fieldProperties[key].type || currentInput.type) as any

    const fieldSpan = fieldProperties[key]?.span ?? 1
    const cn = `input ${getFieldSpanClass(fieldSpan)}`

    return (
      <div className={cn} key={`${key}-${index}`}>
        <RenderInput input={currentInput} Key={key} onChange={onChange} onBlur={onBlur} />
      </div>
    )
  }

  const sectionCount = loanFieldSections.length + (freeSectionFields.length ? 1 : 0)

  return (
    <div className={`grid gap-4 ${gridClass} mb-3`}>
      {loanFieldSections.map(
        (section) =>
          section.fields.length > 0 && (
            <div key={section.id} className={`col-span-full grid gap-4 ${gridClass} mb-3`}>
              {sectionCount > 1 && (
                <div className="col-span-full">
                  <RenderInput
                    input={{ inputType: 'section', title: section.title || '' }}
                    Key={`${section.id}`}
                    onChange={() => {}}
                  />
                </div>
              )}
              {section.fields.map((key, index) => renderInput(key, index))}
            </div>
          ),
      )}
      {freeSectionFields.map((key, index) => renderInput(key, index))}
    </div>
  )
}
